import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Card, Form, Input, Progress, Select, Space, Steps, Table, Tag, Typography, Upload } from 'antd';
import { ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import api from '../../services/api';
import * as whatsappBroadcastService from '../../services/whatsappBroadcastService';
import { WAAudienceFilter, WABroadcast, WABroadcastCreate, WATemplate } from '../../types/whatsapp-broadcast';
import { PropertyArea } from '../../types/property';
import { StatusLead } from '../../types/leads';

const { Title, Text } = Typography;
const { Option } = Select;

const POLL_INTERVAL_MS = 5000;

interface PropertyOption {
  id: string;
  title: string;
}

const statusColor: Record<string, string> = {
  draft: 'default',
  queued: 'blue',
  running: 'processing',
  done: 'success',
  canceled: 'error',
};

const Broadcast: React.FC = () => {
  const { message } = App.useApp();

  // --- lookups ---
  const [templates, setTemplates] = useState<WATemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<PropertyArea[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<StatusLead[]>([]);

  // --- wizard state ---
  const [current, setCurrent] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<WATemplate | null>(null);
  const [bodyParams, setBodyParams] = useState<string[]>([]);
  const [propertyId, setPropertyId] = useState<string | undefined>();
  const [headerImageUrl, setHeaderImageUrl] = useState<string | undefined>();
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [filter, setFilter] = useState<WAAudienceFilter>({});
  const [previewResult, setPreviewResult] = useState<{ count: number; skipped: number } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // --- broadcast list ---
  const [broadcasts, setBroadcasts] = useState<WABroadcast[]>([]);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const r = await whatsappBroadcastService.listTemplates();
      setTemplates((r.data || []).filter((t: WATemplate) => t.enabled && t.status === 'APPROVED'));
    } catch (error) {
      console.error(error);
      message.error('Gagal memuat daftar template');
    } finally {
      setLoadingTemplates(false);
    }
  }, [message]);

  const fetchProperties = useCallback(async () => {
    try {
      const res = await api.get('properties', { params: { limit: 100 } });
      const rows = res.data?.data || [];
      setProperties(rows.map((p: any) => ({ id: p.id, title: p.title || p.name || p.id })));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchPropertyAreas = useCallback(async () => {
    try {
      const res = await api.get('property-areas');
      setPropertyAreas(res.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchLeadStatuses = useCallback(async () => {
    try {
      const res = await api.get('lead-status');
      setLeadStatuses(res.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchBroadcasts = useCallback(
    async (silent = false) => {
      if (!silent) setLoadingBroadcasts(true);
      try {
        const r = await whatsappBroadcastService.listBroadcasts();
        setBroadcasts(r.data || []);
      } catch (error) {
        console.error(error);
        if (!silent) message.error('Gagal memuat daftar broadcast');
      } finally {
        if (!silent) setLoadingBroadcasts(false);
      }
    },
    [message]
  );

  useEffect(() => {
    fetchTemplates();
    fetchProperties();
    fetchPropertyAreas();
    fetchLeadStatuses();
    fetchBroadcasts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll the broadcast list every ~5s while something is queued/running
  useEffect(() => {
    const hasActive = broadcasts.some((b) => b.status === 'running' || b.status === 'queued');
    if (!hasActive) return;
    const interval = setInterval(() => fetchBroadcasts(true), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [broadcasts, fetchBroadcasts]);

  const handleTemplateChange = (name: string) => {
    const t = templates.find((tpl) => tpl.name === name) || null;
    setSelectedTemplate(t);
    setBodyParams(t ? Array(t.body_param_count).fill('') : []);
    setHeaderImageUrl(undefined);
  };

  const handleHeaderUpload = async (file: RcFile) => {
    setUploadingHeader(true);
    try {
      const url = await whatsappBroadcastService.uploadImage(file);
      setHeaderImageUrl(url);
    } catch (error) {
      console.error(error);
      message.error('Gagal mengunggah gambar header');
    } finally {
      setUploadingHeader(false);
    }
    return false;
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const r = await whatsappBroadcastService.previewBroadcast({ filter });
      setPreviewResult({ count: r.data?.count ?? 0, skipped: r.data?.skipped ?? 0 });
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal memuat preview audiens');
    } finally {
      setPreviewing(false);
    }
  };

  const resetWizard = () => {
    setCurrent(0);
    setSelectedTemplate(null);
    setBodyParams([]);
    setPropertyId(undefined);
    setHeaderImageUrl(undefined);
    setFilter({});
    setPreviewResult(null);
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;
    setSubmitting(true);
    try {
      const body: WABroadcastCreate = {
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language,
        property_id: propertyId,
        header_image_url: headerImageUrl,
        body_params: bodyParams,
        filter,
      };
      await whatsappBroadcastService.createBroadcast(body);
      message.success('Broadcast berhasil dibuat, pengiriman berjalan di background');
      resetWizard();
      await fetchBroadcasts(false);
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal membuat broadcast');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async (id: string) => {
    setRetryingId(id);
    try {
      await whatsappBroadcastService.retryBroadcast(id);
      message.success('Broadcast di-retry');
      await fetchBroadcasts(false);
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal retry broadcast');
    } finally {
      setRetryingId(null);
    }
  };

  const canNext = useMemo(() => {
    if (current === 0) return !!selectedTemplate;
    if (current === 1) {
      if (bodyParams.some((p) => !p.trim())) return false;
      if (selectedTemplate?.header_type === 'image' && !headerImageUrl) return false;
      return true;
    }
    return true;
  }, [current, selectedTemplate, bodyParams, headerImageUrl]);

  const steps = [{ title: 'Pilih Template' }, { title: 'Isi Parameter' }, { title: 'Audiens' }, { title: 'Konfirmasi' }];

  return (
    <div>
      <Title level={2}>Broadcast WhatsApp</Title>

      <Card style={{ marginBottom: 24 }} bordered={false}>
        <Steps current={current} items={steps} style={{ marginBottom: 24 }} />

        {current === 0 && (
          <Form layout="vertical">
            <Form.Item label="Template (APPROVED)" required>
              <Select
                placeholder="Pilih template"
                loading={loadingTemplates}
                value={selectedTemplate?.name}
                onChange={handleTemplateChange}
                showSearch
                optionFilterProp="children"
              >
                {templates.map((t) => (
                  <Option key={t.id} value={t.name}>
                    {t.name} ({t.language}) — {t.category}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {templates.length === 0 && !loadingTemplates && (
              <Text type="secondary">
                Belum ada template APPROVED. Buat/sinkronkan dulu di halaman Template WhatsApp.
              </Text>
            )}
          </Form>
        )}

        {current === 1 && selectedTemplate && (
          <Form layout="vertical">
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Isi nilai tiap parameter body — boleh literal teks, atau placeholder{' '}
              <Tag>{'{{lead.full_name}}'}</Tag> / <Tag>{'{{lead.first_name}}'}</Tag> yang otomatis diganti per-lead.
            </Text>
            {bodyParams.map((val, idx) => (
              <Form.Item key={idx} label={`Parameter {{${idx + 1}}}`}>
                <Input
                  value={val}
                  placeholder="literal teks atau {{lead.full_name}}"
                  onChange={(e) => {
                    const next = [...bodyParams];
                    next[idx] = e.target.value;
                    setBodyParams(next);
                  }}
                />
              </Form.Item>
            ))}
            <Form.Item label="Properti yang dipromosikan (opsional)">
              <Select
                allowClear
                placeholder="Pilih properti"
                value={propertyId}
                onChange={(v) => setPropertyId(v)}
                showSearch
                optionFilterProp="children"
              >
                {properties.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {selectedTemplate.header_type === 'image' && (
              <Form.Item label="Gambar Header" required>
                <Upload accept="image/*" showUploadList={false} beforeUpload={handleHeaderUpload}>
                  <Button icon={<UploadOutlined />} loading={uploadingHeader}>
                    {headerImageUrl ? 'Ganti gambar' : 'Unggah gambar'}
                  </Button>
                </Upload>
                {headerImageUrl && (
                  <div style={{ marginTop: 8 }}>
                    <img src={headerImageUrl} alt="header" style={{ maxWidth: 160, borderRadius: 6 }} />
                  </div>
                )}
              </Form.Item>
            )}
          </Form>
        )}

        {current === 2 && (
          <Form layout="vertical">
            <Form.Item label="Area Properti (opsional)">
              <Select
                allowClear
                placeholder="Semua area"
                value={filter.property_area_id}
                onChange={(v) => setFilter((f) => ({ ...f, property_area_id: v }))}
                showSearch
                optionFilterProp="children"
              >
                {propertyAreas.map((a) => (
                  <Option key={a.id} value={a.id}>
                    {a.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Status Lead (opsional)">
              <Select
                allowClear
                placeholder="Semua status"
                value={filter.lead_status_id}
                onChange={(v) => setFilter((f) => ({ ...f, lead_status_id: v }))}
                showSearch
                optionFilterProp="children"
              >
                {leadStatuses.map((s) => (
                  <Option key={s.id} value={s.id}>
                    {s.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Space>
              <Button onClick={handlePreview} loading={previewing}>
                Preview Jumlah
              </Button>
              {previewResult && (
                <Text>
                  Audiens: <strong>{previewResult.count}</strong> lead akan menerima, {previewResult.skipped} dilewati
                  (opt-out / nomor invalid / duplikat).
                </Text>
              )}
            </Space>
          </Form>
        )}

        {current === 3 && selectedTemplate && (
          <div>
            <p>
              Template: <strong>{selectedTemplate.name}</strong> ({selectedTemplate.language})
            </p>
            <p>Parameter: {bodyParams.filter(Boolean).join(', ') || '-'}</p>
            <p>Properti: {properties.find((p) => p.id === propertyId)?.title || '-'}</p>
            <p>
              Audiens:{' '}
              {previewResult ? `${previewResult.count} lead (± ${previewResult.skipped} dilewati)` : 'Belum di-preview'}
            </p>
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={current === 0} onClick={() => setCurrent((c) => c - 1)}>
            Kembali
          </Button>
          {current < steps.length - 1 ? (
            <Button type="primary" disabled={!canNext} onClick={() => setCurrent((c) => c + 1)}>
              Lanjut
            </Button>
          ) : (
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              Kirim Broadcast
            </Button>
          )}
        </div>
      </Card>

      <Card
        title="Daftar Broadcast"
        bordered={false}
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => fetchBroadcasts(false)}>
            Refresh
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loadingBroadcasts}
          dataSource={broadcasts}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Template', dataIndex: 'template_name' },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (status: string) => <Tag color={statusColor[status] || 'default'}>{status}</Tag>,
            },
            {
              title: 'Progress',
              key: 'progress',
              render: (_: any, b: WABroadcast) => {
                const done = b.sent + b.failed + b.skipped;
                const percent = b.total > 0 ? Math.round((done / b.total) * 100) : 0;
                return <Progress percent={percent} size="small" status={b.status === 'done' ? 'success' : 'active'} />;
              },
            },
            {
              title: 'Statistik',
              key: 'stats',
              render: (_: any, b: WABroadcast) => (
                <Space size={4} wrap>
                  <Tag>Total: {b.total}</Tag>
                  <Tag color="blue">Terkirim: {b.sent}</Tag>
                  <Tag color="cyan">Delivered: {b.delivered}</Tag>
                  <Tag color="green">Dibaca: {b.read}</Tag>
                  <Tag color="red">Gagal: {b.failed}</Tag>
                  <Tag>Dilewati: {b.skipped}</Tag>
                </Space>
              ),
            },
            {
              title: 'Dibuat',
              dataIndex: 'created_at',
              render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-'),
            },
            {
              title: 'Aksi',
              key: 'action',
              render: (_: any, b: WABroadcast) =>
                b.failed > 0 ? (
                  <Button size="small" loading={retryingId === b.id} onClick={() => handleRetry(b.id)}>
                    Retry
                  </Button>
                ) : null,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default Broadcast;
